import React, { useState, useEffect } from 'react';
import { TabelasPesoService } from '../services/tabelasPesoService';
import * as XLSX from 'xlsx';
import { IconScale } from '../components/icons';
import './TabelasPeso.css';

function TabelasPeso() {
  const [colunasIdades, setColunasIdades] = useState([]);
  const [linhasCategorias, setLinhasCategorias] = useState([]);
  const [matriz, setMatriz] = useState({});
  const [tabelasSalvas, setTabelasSalvas] = useState([]);
  const [nomeTabelaAtual, setNomeTabelaAtual] = useState('');
  const [tabelaSelecionadaId, setTabelaSelecionadaId] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Custom notifications
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [promptModal, setPromptModal] = useState({ show: false, title: '', type: '', resolve: null });
  const [promptValue, setPromptValue] = useState('');

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
  };

  useEffect(() => {
    carregarTabelasSalvas();
  }, []);

  const carregarTabelasSalvas = async () => {
    try {
      const data = await TabelasPesoService.listar();
      setTabelasSalvas(data);
    } catch (error) {
      console.error('Erro ao carregar tabelas do banco:', error);
    }
  };

  const carregarTabelaPorId = async (id) => {
    if (!id) {
      limparTabela();
      setTabelaSelecionadaId('');
      return;
    }
    setLoading(true);
    try {
      const tabela = await TabelasPesoService.buscarPorId(id);
      setNomeTabelaAtual(tabela.nome);
      setColunasIdades(tabela.colunas || []);
      setLinhasCategorias(tabela.linhas || []);
      setMatriz(tabela.matriz || {});
      setTabelaSelecionadaId(id);
    } catch (error) {
      console.error('Erro ao buscar matriz de pesos:', error);
      showToast('Erro ao carregar a tabela selecionada.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const requestPrompt = (title, type) => {
    return new Promise((resolve) => {
      setPromptValue('');
      setPromptModal({ show: true, title, type, resolve });
    });
  };

  const handlePromptSubmit = () => {
    if (promptModal.resolve) {
      promptModal.resolve(promptValue);
    }
    setPromptModal({ show: false, title: '', type: '', resolve: null });
  };

  const handlePromptCancel = () => {
    if (promptModal.resolve) {
      promptModal.resolve(null);
    }
    setPromptModal({ show: false, title: '', type: '', resolve: null });
  };

  const addColuna = async () => {
    const nome = await requestPrompt("Digite o nome da Idade/Faixa (ex: 18 a 29 anos Adulto):", "coluna");
    if (nome && !colunasIdades.includes(nome)) {
      setColunasIdades([...colunasIdades, nome]);
    }
  };

  const addLinha = async () => {
    const nome = await requestPrompt("Digite o nome da Categoria (ex: PLUMA):", "linha");
    if (nome && !linhasCategorias.includes(nome)) {
      setLinhasCategorias([...linhasCategorias, nome]);
    }
  };

  const atualizarCelula = (linha, coluna, valor) => {
    const novaMatriz = { ...matriz };
    novaMatriz[`${linha}-${coluna}`] = valor;
    setMatriz(novaMatriz);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.name.endsWith('.csv') || file.name.endsWith('.xlsx')) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        processarCSV(json);
      };
      reader.readAsBinaryString(file);
    } else if (file.type.startsWith('image/')) {
      simularLeituraIA(file);
    } else {
      showToast("Por favor, selecione uma planilha CSV/Excel ou uma Imagem.", "warning");
    }
    
    // Reset the input so the same file can be uploaded again if needed
    e.target.value = null;
  };

  const simularLeituraIA = async (file) => {
    setLoading(true);
    showToast("🔍 Lendo imagem com OCR (Tesseract). Aguarde...", "success");

    try {
      const resultado = await TabelasPesoService.extrairIA(file);

      setColunasIdades(resultado.colunas || []);
      setLinhasCategorias(resultado.linhas || []);
      setMatriz(resultado.matriz || {});

      if (resultado.parcial) {
        showToast(
          `⚠️ OCR leu a imagem mas a estrutura precisa de revisão manual. ${resultado.aviso || ''}`,
          "warning"
        );
      } else {
        showToast(
          `✅ Tabela extraída! ${resultado.linhas.length} categorias × ${resultado.colunas.length} colunas. Revise e clique em Salvar.`,
          "success"
        );
      }
    } catch (error) {
      console.error('Erro na extração OCR:', error);
      showToast(`❌ ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const processarCSV = (dados) => {
    if (dados.length < 2) return;
    
    const headers = dados[0].slice(1).map(h => String(h).trim()).filter(h => h);
    setColunasIdades(headers);

    const novasLinhas = [];
    const novaMatriz = {};

    for (let i = 1; i < dados.length; i++) {
      const row = dados[i];
      if (!row || row.length === 0) continue;

      const categoriaName = String(row[0] || '').trim();
      if (!categoriaName) continue;

      novasLinhas.push(categoriaName);

      headers.forEach((coluna, index) => {
        const pesoRaw = row[index + 1];
        const peso = pesoRaw ? String(pesoRaw).trim() : 'NÃO TEM';
        const key = `${categoriaName}-${coluna}`;
        novaMatriz[key] = peso;
      });
    }

    setLinhasCategorias(novasLinhas);
    setMatriz(novaMatriz);
    showToast("Planilha Excel/CSV importada com sucesso para a estrutura da tabela!", "success");
  };

  const salvarMatriz = async () => {
    if (!nomeTabelaAtual.trim()) {
      showToast("Por favor, digite um nome para esta tabela de pesos no campo acima.", "warning");
      return;
    }

    if (colunasIdades.length === 0 || linhasCategorias.length === 0) {
      showToast("A tabela está vazia. Adicione colunas/linhas ou importe uma planilha.", "warning");
      return;
    }

    const payload = {
      nome: nomeTabelaAtual,
      colunas: colunasIdades,
      linhas: linhasCategorias,
      matriz: matriz
    };

    setLoading(true);
    try {
      if (tabelaSelecionadaId) {
        await TabelasPesoService.atualizar(tabelaSelecionadaId, payload);
        showToast('Tabela atualizada com sucesso no banco de dados!', 'success');
      } else {
        await TabelasPesoService.criar(payload);
        showToast('Nova Tabela salva com sucesso no banco de dados!', 'success');
      }
      carregarTabelasSalvas();
    } catch (error) {
      console.error(error);
      showToast('Erro ao salvar no banco de dados.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const limparTabela = () => {
    setColunasIdades([]);
    setLinhasCategorias([]);
    setMatriz({});
    setNomeTabelaAtual('');
    setTabelaSelecionadaId('');
  };

  const getCellColor = (peso) => {
    const val = (peso || '').toLowerCase();
    if (!val || val === 'não tem' || val === 'nao tem') return '#86efac'; // Verde claro para NÃO TEM
    if (val.includes('absoluto') || val === '-') return '#fef08a'; // Amarelo para Absoluto
    if (val.includes('kg')) return '#bbf7d0'; // Verde um pouco mais escuro para pesos
    return '#f3f4f6';
  };

  const getRowColor = (linha) => {
    const val = (linha || '').toLowerCase();
    if (val === 'médio' || val === 'medio') return '#ef4444'; // Vermelho
    if (val === 'absoluto') return '#fef08a'; // Amarelo
    return '#fdfa72'; // Amarelinho claro padrao da lateral
  };

  const showEditor = nomeTabelaAtual.trim().length > 0 || tabelaSelecionadaId !== '';

  return (
    <div className="tabelas-peso-container" style={{ position: 'relative' }}>
      
      {/* Sistema de Notificação em Tela (Toast) */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          background: toast.type === 'error' ? '#EF4444' : toast.type === 'warning' ? '#F59E0B' : '#10B981',
          color: 'white',
          fontWeight: 'bold',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          maxWidth: '400px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <span>{toast.type === 'error' ? '❌' : toast.type === 'warning' ? '⚠️' : '✅'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Modal Personalizado para Prompt (Substitui window.prompt) */}
      {promptModal.show && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000
        }}>
          <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', width: '400px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#111827' }}>{promptModal.title}</h3>
            <input 
              type="text"
              value={promptValue}
              onChange={(e) => setPromptValue(e.target.value)}
              placeholder="Digite aqui..."
              style={{ width: '100%', padding: '12px', border: '1px solid #D1D5DB', borderRadius: '6px', marginBottom: '20px' }}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handlePromptSubmit(); }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={handlePromptCancel} style={{ padding: '8px 16px', background: '#F3F4F6', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Cancelar</button>
              <button onClick={handlePromptSubmit} style={{ padding: '8px 16px', background: '#3B82F6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Adicionar</button>
            </div>
          </div>
        </div>
      )}

      <div className="tabela-header-section" style={{ background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div className="header-titles">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><IconScale /> Matriz de Pesos (GI / NO-GI)</h1>
          <p>Importe planilhas Excel/CSV ou crie matrizes personalizadas para usar nos campeonatos.</p>
        </div>
        
        <div className="tabela-toolbar" style={{ display: 'flex', gap: '15px', marginTop: '15px', flexWrap: 'wrap' }}>
          <select 
            value={tabelaSelecionadaId} 
            onChange={(e) => carregarTabelaPorId(e.target.value)}
            className="select-tabela"
            disabled={loading}
            style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc', minWidth: '250px' }}
          >
            <option value="">-- Visualizar Tabelas --</option>
            {tabelasSalvas.map(t => (
              <option key={t.id} value={t.id}>{t.nome}</option>
            ))}
          </select>
          
          <label className="import-btn-label" style={{ display: 'flex', alignItems: 'center', background: '#f3f4f6', padding: '10px 15px', borderRadius: '5px', cursor: 'pointer', border: '1px solid #d1d5db', color: '#374151', fontWeight: 'bold' }}>
            ⬆️ Importar Excel (.csv/.xlsx)
            <input 
              type="file" 
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" 
              onChange={handleFileUpload} 
              style={{ display: 'none' }}
            />
          </label>

          <label className="import-btn-label" style={{ display: 'flex', alignItems: 'center', background: '#f3f4f6', padding: '10px 15px', borderRadius: '5px', cursor: 'pointer', border: '1px solid #d1d5db', color: '#374151', fontWeight: 'bold' }}>
            📎 Anexar Arquivo/Imagem
            <input 
              type="file" 
              accept="image/*,.pdf,.xlsx,.csv" 
              onChange={handleFileUpload} 
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      <div className="tabela-config-row" style={{ marginTop: '20px' }}>
        <div className="form-group">
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Nome da Tabela de Pesos *</label>
          <input 
            type="text" 
            placeholder="Ex: Padrão CBJJ 2026" 
            value={nomeTabelaAtual} 
            onChange={e => setNomeTabelaAtual(e.target.value)} 
            disabled={loading}
            style={{ width: '100%', maxWidth: '400px', padding: '12px', borderRadius: '5px', border: '1px solid #ccc' }}
          />
        </div>
      </div>

      {!showEditor ? (
        <div className="empty-state" style={{ marginTop: '20px', padding: '60px', textAlign: 'center', background: 'white', borderRadius: '12px', border: '2px dashed #E5E7EB' }}>
          <span style={{ fontSize: '48px' }}>📊</span>
          <h3 style={{ margin: '16px 0 8px' }}>Nenhuma matriz configurada</h3>
          <p style={{ margin: 0, color: '#6B7280' }}>Selecione uma tabela existente ou digite um nome acima para criar uma nova.</p>
        </div>
      ) : (
        <div className="editor-section" style={{ marginTop: '20px', background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div className="editor-toolbar" style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button onClick={addColuna} className="btn-secondary">➕ Adicionar Idade (Coluna)</button>
            <button onClick={addLinha} className="btn-secondary">➕ Adicionar Categoria (Linha)</button>
            <button onClick={limparTabela} className="btn-danger-ghost">🗑️ Limpar Tabela</button>
            <div style={{ flex: 1 }}></div>
            <button onClick={salvarMatriz} disabled={loading} className="btn-primary">
              {loading ? 'Salvando...' : '💾 Salvar Matriz Atual'}
            </button>
          </div>

          <div className="weights-scroll">
            <table className="weights-table">
              <thead>
                <tr>
                  <th rowSpan="2" style={{ backgroundColor: '#F3F4F6', padding: '15px', border: '1px solid #E5E7EB', width: '150px' }}>CATEGORIA</th>
                  <th colSpan={colunasIdades.length || 1} style={{ backgroundColor: '#E5E7EB', padding: '12px', border: '1px solid #D1D5DB', textAlign: 'center', letterSpacing: '2px' }}>IDADE</th>
                </tr>
                <tr>
                  {colunasIdades.length === 0 ? (
                    <th style={{ padding: '15px', border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB', color: '#9CA3AF' }}>Adicione colunas</th>
                  ) : (
                    colunasIdades.map((col, idx) => (
                      <th key={idx} style={{ padding: '10px', border: '1px solid #E5E7EB', backgroundColor: '#DBEAFE', textAlign: 'center', fontWeight: '600' }}>{col}</th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {linhasCategorias.length === 0 ? (
                  <tr>
                    <td colSpan={colunasIdades.length + 1} style={{ padding: '30px', textAlign: 'center', color: '#6B7280' }}>Adicione categorias (linhas) e idades (colunas) para montar a tabela.</td>
                  </tr>
                ) : (
                  linhasCategorias.map((linha, idxLinha) => (
                    <tr key={idxLinha}>
                      <td style={{ padding: '10px', border: '1px solid #E5E7EB', fontWeight: 'bold', backgroundColor: getRowColor(linha), textAlign: 'center' }}>{linha}</td>
                      {colunasIdades.map((col, idxCol) => {
                        const key = `${linha}-${col}`;
                        const peso = matriz[key] || '';
                        return (
                          <td key={idxCol} style={{ border: '1px solid #E5E7EB', padding: 0 }}>
                            <input 
                              type="text"
                              value={peso}
                              onChange={(e) => atualizarCelula(linha, col, e.target.value)}
                              placeholder="NÃO TEM"
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                padding: '15px 10px', 
                                border: 'none', 
                                textAlign: 'center',
                                backgroundColor: getCellColor(peso),
                                color: '#111827',
                                fontWeight: peso && peso.toLowerCase() !== 'não tem' ? '600' : '400',
                                outline: 'none'
                              }}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default TabelasPeso;
